import { controller as telemetry } from "../observability/web/runtime";
import { safely } from "../observability/web/contracts";
import {
  RenderedRoute,
  useHomeReport,
} from "../observability/web/HomeNavigation";
import { GA_ACTION, GA_CATEGORIES, GA_LABEL2, PAGE_URLS } from "./constants";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { Suspense, useEffect } from "react";
import { triggerGa } from "./gaUtils";
import { setIgProductTagLoginEnabled } from "./common";
import { useSelector } from "react-redux";

import FooterContainer from "../components/FooterContainer";
import LoadingPage from "../components/Fallback/LoadingPage";
import { validateRouteAccess } from "./Router/router.utils";
import { buildUserDataWithFTUX } from "./ftuxHelper";
import { useGetCreatorConfigQuery } from "../redux/efficientReduxAPI";
import {
  useUtmTracker,
  usePushPhoneNumberToDataLayer,
} from "hooks/useUtmTracker";
import { useAuthStore } from "stores/auth.store";
import CreatorSessionTracker from "components/CreatorSessionTracker";
import GlobalOverlays from "components/GlobalOverlays";
import SetupJourneyOverlay from "components/SetupJourneyOverlay";
import SetupJourneyPushSurface from "components/SetupJourneyOverlay/SetupJourneyPushSurface";
import { useIsSetupJourneyOverlayMounted } from "components/SetupJourneyOverlay/useSetupJourneyOverlay";
import CreatorAnalyticsIdentity from "components/CreatorAnalyticsIdentity";
import AppUtmSync from "components/AppUtmSync";

const CustomRedirect = ({ to, redirectingMessage }) => {
  useHomeReport({ redirecting: true });
  const location = useLocation();
  useEffect(() => {
    if (location.pathname === "/new-product")
      safely(
        () => telemetry.diy.capture()?.finish("access_redirect"),
        undefined
      );
  }, [location.pathname]);
  const { pageName, screenName } = useSelector((state) => state.appReducer);
  const [searchParams] = useSearchParams();
  useEffect(() => {
    if (redirectingMessage) {
      triggerGa({
        category: GA_CATEGORIES.NAVIGATION,
        action: GA_ACTION.REDIRECT,
        label: to,
        label2: pageName,
        custom: screenName,
      });
    }
    if (
      redirectingMessage === "Share to Wishlink feature not available" &&
      searchParams.get("shared_url")
    ) {
      triggerGa({
        category: GA_CATEGORIES.USER_INTERACTION,
        action: GA_ACTION.CTA_CLICK,
        label: searchParams.get("shared_url"),
        label2: GA_LABEL2.SHARE_TO_WISHLINK_LOCKED_REDIRECT,
      });
    }
  }, []);
  return <Navigate to={to} replace={true} />;
};

const EMPTY_USER_DATA = { auth_level: "", authorizations: {} };

const getRouteElement = ({
  route,
  userData,
  creatorConfigError,
  hideFooter,
}) => {
  // data is the mutable copy of userData. We need this as we are modifying the userData in case of creatorConfigError
  let data = userData || EMPTY_USER_DATA;

  // Handle network/fetch errors - throw to be caught by ErrorBoundary
  if (
    creatorConfigError &&
    creatorConfigError?.originalStatus &&
    creatorConfigError?.originalStatus !== 200
  ) {
    throw new Error(
      `Network error: ${creatorConfigError?.status}. Please check your connection and try again.`
    );
  }

  // Handle API errors - replace with empty data structure
  if (
    creatorConfigError?.status === 403 ||
    creatorConfigError?.status === 500
  ) {
    data = EMPTY_USER_DATA;
  }

  // this is super important, if the creator is blocked, we should not allow them to access any page
  if (data?.is_creator_blocked === true) {
    return <CustomRedirect to={PAGE_URLS.USER_BLOCKED} />;
  }

  // validate the route access
  const { isAllowed, fallbackUrl, redirectingMessage } = validateRouteAccess({
    userData: data,
    route,
    customFallback: route.customFallback,
  });

  // In case of route is not allowed by authentication or authorization redirect to fallback url
  if (!isAllowed) {
    const redirectUrl = fallbackUrl || PAGE_URLS.FIREBASELOGIN;
    return (
      <CustomRedirect
        to={redirectUrl}
        redirectingMessage={redirectingMessage}
      />
    );
  }

  // In case of route is permanently moved to another route redirect to that route
  if (route?.permanentlyMovedTo) {
    const redirectUrl = route?.permanentlyMovedTo || PAGE_URLS.FIREBASELOGIN;
    const redirectingMessage =
      "Redirecting to new route, as the route has been permanently moved";
    return (
      <CustomRedirect
        to={redirectUrl}
        redirectingMessage={redirectingMessage}
      />
    );
  }

  let RenderedComponent;
  if (typeof route.component === "function") {
    // Dynamic component - call resolver with userData
    RenderedComponent = route.component(userData);
  } else {
    // Static component - use directly
    RenderedComponent = route.component;
  }
  const routeProps = route.props || {};
  return (
    <RenderedRoute>
      <Suspense fallback={<LoadingPage />}>
        <RenderedComponent {...routeProps} />
      </Suspense>
      {/* CAT-407 — the journey is a full screen, so the bottom nav goes away
          with it, exactly as it did when /setup-journey was a route with
          `showFooter: false`. It is also the one fixed element inside the push
          surface that the creator would actually catch mid-transform: pushed,
          its `bottom: 0` resolves against the full page height instead of the
          viewport. Hidden for the whole of both animations, not just while the
          overlay is open, because the transform is still interpolating for a
          screen duration after the close. */}
      {route.showFooter && !creatorConfigError && !hideFooter && (
        <FooterContainer />
      )}
    </RenderedRoute>
  );
};

const RenderRoutes = (props) => {
  const { routes } = props;
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isLoggedIn, backendLink } = useAuthStore();

  // `isLoggedIn` already covers admins (they authenticate via adminToken and
  // never sign into Firebase), so no separate admin check is needed here.
  const shouldSkipApi = !isLoggedIn || backendLink === "pending";

  const {
    data: creatorConfig,
    isLoading: isCreatorConfigLoading,
    error: creatorConfigError,
    isError: creatorConfigIsError,
  } = useGetCreatorConfigQuery(undefined, {
    skip: shouldSkipApi,
  });

  useUtmTracker();
  usePushPhoneNumberToDataLayer(creatorConfig?.phone_number);

  // Facebook Connect asks for IG product tag permissions only for flagged creators.
  const isIgProductTagEnabled =
    !!creatorConfig?.authorizations?.IG_PRODUCT_TAG__ENABLED?.achieved;
  useEffect(() => {
    setIgProductTagLoginEnabled(isIgProductTagEnabled);
  }, [isIgProductTagEnabled]);

  const isSetupJourneyOverlayMounted = useIsSetupJourneyOverlayMounted();

  useEffect(() => {
    const redirectFirebaseToken = searchParams?.get("ft");
    const redirectApp = searchParams?.get("app");
    if (redirectFirebaseToken) {
      localStorage.setItem("firebaseToken", redirectFirebaseToken);
      localStorage.setItem("loginApp", redirectApp);
    }
  }, [location.pathname]);

  // Firebase has a user but the creator is not linked yet — routes would read
  // a half-provisioned account.
  const isLinking = backendLink === "pending";
  const isConfigOk = !!creatorConfigError || isCreatorConfigLoading === false;
  const shouldShowRoutes = isConfigOk && !isLinking;

  const userData = buildUserDataWithFTUX(creatorConfig);

  return (
    <>
      <CreatorSessionTracker creatorId={creatorConfig?.creator_id} />
      <CreatorAnalyticsIdentity creatorConfig={creatorConfig} />
      <AppUtmSync creatorId={creatorConfig?.creator_id} />
      {shouldShowRoutes ? (
        <>
          {/* CAT-407 — the routed page is what the setup journey overlay slides
              over and pushes, so it needs an element of its own to transform.
              See SetupJourneyPushSurface for why the transform is scoped. */}
          <SetupJourneyPushSurface>
            <Routes>
              {routes.map((route, idx) => (
                <Route
                  key={idx}
                  path={route.path}
                  element={getRouteElement({
                    route,
                    userData,
                    creatorConfigError,
                    hideFooter: isSetupJourneyOverlayMounted,
                  })}
                />
              ))}
            </Routes>
          </SetupJourneyPushSurface>
          <GlobalOverlays
            creatorConfig={creatorConfig}
            hasConfigError={creatorConfigIsError}
          />
          <SetupJourneyOverlay />
        </>
      ) : (
        <LoadingPage />
      )}
    </>
  );
};

export default RenderRoutes;
